import React, { useState } from 'react';
import { Instagram, Menu, ChevronDown } from 'lucide-react';
import heroImage from '../assets/hero-farzeen.png';
import FloatingChatButton from '../components/FloatingChatButton';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    enquiryType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your enquiry! I\'ll get back to you within 48 hours.');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#3d3d35' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 py-6 px-8">
        <div className="container mx-auto flex items-center justify-between">
          {/* Left Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a 
              href="/" 
              className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60"
              style={{ color: 'rgba(255,255,255,0.8)' }}
            >
              HOME
            </a>
            <a 
              href="/#about" 
              className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60"
              style={{ color: 'rgba(255,255,255,0.8)' }}
            >
              ABOUT
            </a>
            <a 
              href="/#services" 
              className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60"
              style={{ color: 'rgba(255,255,255,0.8)' }}
            >
              SERVICES
            </a>
            <a 
              href="/contact" 
              className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60 underline underline-offset-4"
              style={{ color: 'rgba(255,255,255,1)' }}
            >
              CONTACT
            </a>
          </div>
          
          {/* Center Logo */}
          <a 
            href="/" 
            className="text-sm tracking-[0.3em] font-light hover:opacity-70 transition-opacity italic"
            style={{ color: '#ffffff', fontFamily: "'Playfair Display', serif" }}
          >
            farzeen
          </a>
          
          {/* Right - Social Icons */}
          <div className="hidden md:flex items-center gap-6">
            <a 
              href="https://instagram.com/itsfarzeenkhan" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-60"
              style={{ color: '#ffffff' }}
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>
          </div>
          
          {/* Mobile Menu */}
          <button className="md:hidden" style={{ color: '#ffffff' }}>
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-8 pt-32 pb-20">
        <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left - Image */}
          <div className="relative">
            <img 
              src={heroImage} 
              alt="Farzeen" 
              className="w-full h-auto object-cover"
              style={{ maxHeight: '80vh' }}
            />
          </div>
          
          {/* Right - Form */}
          <div className="pt-8">
            {/* Book Call Button */}
            <a 
              href="https://stan.store/FarzeenDigitalWealth/p/lets-chat-book-your-free-15-min-clarity-call"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-5 text-center text-xs tracking-[0.2em] font-light mb-16 transition-all hover:opacity-80"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              BOOK 1-1 WITH FARZEEN
            </a>
            
            {/* Enquiry Section */}
            <div>
              <h2 
                className="text-xl md:text-2xl tracking-[0.2em] font-light mb-6"
                style={{ color: '#ffffff' }}
              >
                HAVE AN ENQUIRY?
              </h2>
              
              <p 
                className="text-sm font-light leading-relaxed mb-2"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Maybe you need a little more information on my mentorship programs, or perhaps you're wondering if DWA is for you? You're in the right place.
              </p>
              
              <p 
                className="text-sm font-light leading-relaxed mb-10"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                Pop your question below, and you'll hear back within the next 48 hours.
              </p>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Name Fields */}
                <div>
                  <label className="block text-sm font-light mb-4" style={{ color: '#ffffff' }}>
                    Name
                  </label>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-light mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        First Name <span style={{ color: 'rgba(255,255,255,0.4)' }}>(required)</span>
                      </label>
                      <input 
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b py-2 text-sm font-light focus:outline-none transition-colors"
                        style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-light mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        Last Name <span style={{ color: 'rgba(255,255,255,0.4)' }}>(required)</span>
                      </label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b py-2 text-sm font-light focus:outline-none transition-colors"
                        style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-xs font-light mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Email <span style={{ color: 'rgba(255,255,255,0.4)' }}>(required)</span>
                  </label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b py-2 text-sm font-light focus:outline-none transition-colors"
                    style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                  />
                </div>
                
                {/* Enquiry Type */}
                <div>
                  <label className="block text-xs font-light mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    I'm enquiring about <span style={{ color: 'rgba(255,255,255,0.4)' }}>(required)</span>
                  </label>
                  <div className="relative">
                    <select 
                      name="enquiryType"
                      value={formData.enquiryType}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b py-2 text-sm font-light focus:outline-none appearance-none cursor-pointer"
                      style={{ borderColor: 'rgba(255,255,255,0.3)', color: formData.enquiryType ? '#ffffff' : 'rgba(255,255,255,0.5)' }}
                    >
                      <option value="" disabled>Select an option</option>
                      <option value="mentorship">1-1 Mentorship</option>
                      <option value="dwa">Digital Wealth Academy</option>
                      <option value="branding">Branding Services</option>
                      <option value="collaboration">Collaboration / Partnership</option>
                      <option value="other">Other</option>
                    </select>
                    <ChevronDown 
                      size={16} 
                      className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    />
                  </div>
                </div>
                
                {/* Message */}
                <div>
                  <label className="block text-xs font-light mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Message <span style={{ color: 'rgba(255,255,255,0.4)' }}>(required)</span>
                  </label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-transparent border-b py-2 text-sm font-light focus:outline-none resize-none transition-colors"
                    style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#ffffff' }}
                  />
                </div>
                
                {/* Submit Button */}
                <button 
                  type="submit"
                  className="text-xs tracking-[0.2em] font-light py-3 px-8 transition-all hover:opacity-80"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  SEND
                </button>
              </form>
              
              {/* Stay Connected */}
              <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="text-sm tracking-[0.15em] font-light mb-4" style={{ color: '#ffffff' }}>
                  Stay Connected
                </h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com/itsfarzeenkhan" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-opacity hover:opacity-60"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    <Instagram size={20} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Instagram Chat Button */}
      <FloatingChatButton />
    </div>
  );
};

export default ContactPage;
